import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  WalletIcon,
  UserIcon,
  CogIcon,
  BellIcon,
  ChartBarIcon,
  UsersIcon,
  CreditCardIcon,
  CubeIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
  DevicePhoneMobileIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  BugAntIcon,
  PhoneIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../hooks/useAuth';
import { User } from '../../types';
import HelpCenter from '../common/HelpCenter';
import Badge from '../common/Badge';
import BrandLogo from '../common/BrandLogo';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, user }) => {
  const location = useLocation();
  const isActive = React.useCallback((href: string) => {
    const raw = String(href || '');
    const [hrefPath, hrefHash] = raw.split('#');
    const [hrefPathWithoutQuery] = hrefPath.split('?');
    const normalizedPath = hrefPathWithoutQuery || '';
    const normalizedHash = hrefHash ? `#${hrefHash}` : '';

    // Hash links must match both pathname and hash
    if (normalizedHash) {
      return location.pathname === normalizedPath && location.hash === normalizedHash;
    }

    // Special-case root pages: match exact pathname; for /wallet, only match when no hash is present
    if (normalizedPath === '/dashboard' || normalizedPath === '/wallet' || normalizedPath === '/profile' || normalizedPath === '/settings') {
      if (normalizedPath === '/wallet') {
        const searchParams = new URLSearchParams(location.search);
        const isTopUp = searchParams.get('topup') === '1' || location.search.includes('topup=1');
        if (href.includes('topup=1')) {
          return location.pathname === '/wallet' && isTopUp;
        }
        return location.pathname === normalizedPath && !isTopUp && (!location.hash || location.hash === '#');
      }
      return location.pathname === normalizedPath;
    }

    return location.pathname.startsWith(normalizedPath);
  }, [location.pathname, location.hash, location.search]);
  const { logout, features } = useAuth();
  const hasFeature = React.useCallback(
    (feature: string) => !!features && features.includes(feature),
    [features]
  );
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const getRoleNavigation = React.useCallback((user: User | null) => {
    const role = user?.role || 'user';

    if (role === 'admin') {
      return [
        {
          section: 'Dashboard',
          title: 'Dashboard',
          description: 'Overview, KPIs, and quick actions',
          icon: 'ChartBarIcon',
          href: '/admin',
          roles: ['admin'],
          order: 10,
        },
        {
          section: 'Analytics & Reports',
          title: 'Analytics',
          description: 'Revenue and performance reports',
          icon: 'ArrowTrendingUpIcon',
          href: '/admin/analytics',
          roles: ['admin'],
          order: 15,
        },

        {
          section: 'System Configuration',
          title: 'Supplier Routing',
          description: 'Per-network supplier routing rules and failover',
          icon: 'ClipboardDocumentListIcon',
          href: '/admin/routing',
          roles: ['admin'],
          order: 16,
        },
        {
          section: 'System Configuration',
          title: 'Backfill Diagnostics',
          description: 'Inspect supplier cost backfill status',
          icon: 'BugAntIcon',
          href: '/admin/backfill-diagnostics',
          roles: ['admin'],
          order: 17,
        },
        {
          section: 'Users & Roles',
          title: 'Users Management',
          description: 'Users, admins, and agents',
          icon: 'UsersIcon',
          href: '/admin/users',
          roles: ['admin'],
          order: 20,
        },
        {
          section: 'Users & Roles',
          title: 'Export Center',
          description: 'Download users & recipients',
          icon: 'ClipboardDocumentListIcon',
          href: '/admin/users/export',
          roles: ['admin'],
          order: 21,
        },
        {
          section: 'Orders & Sales',
          title: 'Orders',
          description: 'Manage individual orders',
          icon: 'ShoppingBagIcon',
          href: '/admin/orders',
          roles: ['admin'],
          order: 30,
        },
        {
          section: 'Orders & Sales',
          title: 'Airtime Orders',
          description: 'Manage airtime purchases',
          icon: 'PhoneIcon',
          href: '/admin/airtime-orders',
          roles: ['admin'],
          order: 31,
        },
        {
          section: 'Orders & Sales',
          title: 'Bulk Orders',
          description: 'Process bulk orders',
          icon: 'UserGroupIcon',
          href: '/admin/bulk-orders',
          roles: ['admin'],
          order: 32,
        },
        {
          section: 'Orders & Sales',
          title: 'MTN AFA Applications',
          description: 'Review AFA requests',
          icon: 'BuildingOfficeIcon',
          href: '/admin/mtn-afa',
          roles: ['admin'],
          order: 32,
        },
        {
          section: 'Orders & Sales',
          title: 'WAEC Applications',
          description: 'Manage WAEC stock',
          icon: 'DocumentTextIcon',
          href: '/admin/waec-stock',
          roles: ['admin'],
          order: 33,
        },
        {
          section: 'Wallet & Finance',
          title: 'Transactions',
          description: 'Payments and transaction history',
          icon: 'CreditCardIcon',
          href: '/admin/payments',
          roles: ['admin'],
          order: 40,
        },
        {
          section: 'Wallet & Finance',
          title: 'Wallet Top-ups',
          description: 'Credits, debits, audit, and commissions',
          icon: 'BanknotesIcon',
          href: '/admin/wallet-topup',
          roles: ['admin'],
          order: 41,
        },
        {
          section: 'Wallet & Finance',
          title: 'Withdrawals',
          description: 'Review and process withdrawals',
          icon: 'BanknotesIcon',
          href: '/admin/withdrawals',
          roles: ['admin'],
          order: 42,
        },
        {
          section: 'Wallet & Finance',
          title: 'Payment Verification',
          description: 'Verify pending payments',
          icon: 'ShieldCheckIcon',
          href: '/admin/payment-verification',
          roles: ['admin'],
          order: 43,
        },
        {
          section: 'Wallet & Finance',
          title: 'Agent Registration Fees',
          description: 'Configure agent payment settings',
          icon: 'CurrencyDollarIcon',
          href: '/admin/agent-payment',
          roles: ['admin'],
          order: 45,
        },
        {
          section: 'Wallet & Finance',
          title: 'Agent Commissions',
          description: 'Storefront commission analytics and export',
          icon: 'CurrencyDollarIcon',
          href: '/admin/commissions',
          roles: ['admin'],
          order: 46,
        },
        {
          section: 'Analytics & Reports',
          title: 'Sales Ladder',
          description: 'Customer spending rankings and analytics',
          icon: 'ChartBarIcon',
          href: '/admin/sales-ladder',
          roles: ['admin'],
          order: 45,
        },
        {
          section: 'Packages & Products',
          title: 'Data Packages',
          description: 'Manage bundles catalog',
          icon: 'CubeIcon',
          href: '/admin/packages',
          roles: ['admin'],
          order: 50,
        },
        {
          section: 'Packages & Products',
          title: 'Discounts',
          description: 'Manage discounts and flash sales',
          icon: 'TagIcon',
          href: '/admin/discounts',
          roles: ['admin'],
          order: 51,
        },
        {
          section: 'Packages & Products',
          title: 'Pricing & Fees',
          description: 'AFA and WAEC pricing settings',
          icon: 'CurrencyDollarIcon',
          href: '/admin/pricing',
          roles: ['admin'],
          order: 52,
        },
        {
          section: 'System Configuration',
          title: 'System Settings',
          description: 'Feature toggles and app configuration',
          icon: 'CogIcon',
          href: '/admin/settings',
          roles: ['admin'],
          order: 60,
        },
        {
          section: 'System Configuration',
          title: 'Notifications',
          description: 'Telegram notification settings',
          icon: 'BellIcon',
          href: '/admin/telegram',
          roles: ['admin'],
          order: 62,
        },
        {
          section: 'System Configuration',
          title: 'Announcements',
          description: 'Manage system announcements',
          icon: 'ClipboardDocumentListIcon',
          href: '/admin/announcements',
          roles: ['admin'],
          order: 63,
        },
        {
          title: 'Delivery Status',
          description: 'Manage delivery alerts on storefront banner',
          icon: 'TruckIcon',
          href: '/admin/delivery-status',
          roles: ['admin'],
          order: 64,
        },
      ];
    }

    if (role === 'agent') {
      return [
        {
          section: 'Dashboard',
          title: 'Dashboard',
          description: 'Quick actions and performance',
          icon: 'HomeIcon',
          href: '/dashboard',
          roles: ['agent'],
          order: 10,
        },
        {
          section: 'Dashboard',
          title: 'Agent Dashboard',
          description: 'Agent analytics',
          icon: 'ChartBarIcon',
          href: '/agent',
          roles: ['agent'],
          order: 11,
        },
        ...(hasFeature('airtime_purchase')
          ? [
              {
                section: 'Services',
                title: 'Airtime',
                description: 'Top up any phone',
                icon: 'DevicePhoneMobileIcon',
                href: '/airtime',
                roles: ['agent'],
                order: 15,
                badge: 'New',
              },
            ]
          : []),
        {
          section: 'Services',
          title: 'Data Bundles',
          description: 'Browse and buy data packages',
          icon: 'CubeIcon',
          href: '/packages',
          roles: ['agent'],
          order: 12,
        },
        {
          section: 'Services',
          title: 'MTN AFA',
          description: 'MTN AFA registrations',
          icon: 'BuildingOfficeIcon',
          href: '/mtn-afa',
          roles: ['agent'],
          order: 13,
        },
        {
          section: 'Services',
          title: 'WAEC',
          description: 'WAEC vouchers and checkers',
          icon: 'DocumentTextIcon',
          href: '/waec',
          roles: ['agent'],
          order: 14,
        },
        {
          section: 'Services',
          title: 'Bulk Orders',
          description: 'Place bulk data orders',
          icon: 'UserGroupIcon',
          href: '/bulk-orders',
          roles: ['agent'],
          order: 16,
        },
        {
          section: 'Orders',
          title: 'Orders',
          description: 'Agent order history',
          icon: 'DocumentTextIcon',
          href: '/orders',
          roles: ['agent'],
          order: 20,
        },
        {
          section: 'Orders',
          title: 'Checker History',
          description: 'WAEC voucher purchase history & PINs',
          icon: 'ClipboardDocumentListIcon',
          href: '/waec/history',
          roles: ['agent'],
          order: 22,
        },
        {
          section: 'Orders',
          title: 'Price Watchlist',
          description: 'Track price drops on packages',
          icon: 'BellIcon',
          href: '/watchlist',
          roles: ['agent'],
          order: 23,
        },
        {
          section: 'Orders',
          title: 'Bulk Order History',
          description: 'View bulk order history',
          icon: 'ClipboardDocumentListIcon',
          href: '/bulk-orders/history',
          roles: ['agent'],
          order: 21,
        },
        {
          section: 'Wallet',
          title: 'Wallet',
          description: 'Wallet balance',
          icon: 'WalletIcon',
          href: '/wallet',
          roles: ['agent'],
          order: 30,
        },
        {
          section: 'Wallet',
          title: 'Deposit',
          description: 'Add funds to your wallet',
          icon: 'BanknotesIcon',
          href: '/wallet?topup=1',
          roles: ['agent'],
          order: 31,
        },
        {
          section: 'Wallet',
          title: 'Commission Earnings',
          description: 'Commission summary',
          icon: 'CurrencyDollarIcon',
          href: '/commissions',
          roles: ['agent'],
          order: 32,
        },
        {
          section: 'Wallet',
          title: 'Commission Withdrawal',
          description: 'Withdraw commissions to MoMo or bank',
          icon: 'BanknotesIcon',
          href: '/withdrawals',
          roles: ['agent'],
          order: 33,
        },
        {
          section: 'Wallet',
          title: 'Wallet Transactions',
          description: 'Payment and transaction history',
          icon: 'CreditCardIcon',
          href: '/payments',
          roles: ['agent'],
          order: 34,
        },
        {
          section: 'Settings',
          title: 'Storefront',
          description: 'Storefront settings and pricing',
          icon: 'BuildingOfficeIcon',
          href: '/storefront',
          roles: ['agent'],
          order: 39,
        },
        {
          section: 'Settings',
          title: 'Settings',
          description: 'Profile and preferences',
          icon: 'CogIcon',
          href: '/settings',
          roles: ['agent'],
          order: 40,
        },
        {
          section: 'Settings',
          title: 'Profile',
          description: 'View and edit your profile',
          icon: 'UserIcon',
          href: '/profile',
          roles: ['agent'],
          order: 41,
        },
        {
          section: 'Settings',
          title: 'Customers',
          description: 'Manage your customers',
          icon: 'UsersIcon',
          href: '/customers',
          roles: ['agent'],
          order: 42,
        },
      ];
    }

    return [
      {
        section: 'Dashboard',
        title: 'Dashboard',
        description: 'Quick actions and usage',
        icon: 'HomeIcon',
        href: '/dashboard',
        roles: ['user'],
        order: 10,
      },
      ...(hasFeature('airtime_purchase')
        ? [
            {
              section: 'Services',
              title: 'Airtime',
              description: 'Top up any phone',
              icon: 'DevicePhoneMobileIcon',
              href: '/airtime',
              roles: ['user'],
              order: 15,
              badge: 'New',
            },
          ]
        : []),
      ...(hasFeature('referrals')
        ? [
            {
              section: 'Business',
              title: 'Referrals',
              description: 'Earn rewards',
              icon: 'GiftIcon',
              href: '/referrals',
              roles: ['user'],
              order: 16,
              badge: 'New',
            },
          ]
        : []),
      {
        section: 'Services',
        title: 'Data Bundles',
        description: 'Browse and buy data packages',
        icon: 'CubeIcon',
        href: '/packages',
        roles: ['user'],
        order: 12,
      },
      {
        section: 'Services',
        title: 'MTN AFA',
        description: 'MTN AFA registrations',
        icon: 'BuildingOfficeIcon',
        href: '/mtn-afa',
        roles: ['user'],
        order: 13,
      },
      {
        section: 'Services',
        title: 'WAEC',
        description: 'WAEC vouchers and checkers',
        icon: 'DocumentTextIcon',
        href: '/waec',
        roles: ['user'],
        order: 14,
      },
      {
        section: 'Services',
        title: 'Bulk Orders',
        description: 'Place bulk data orders',
        icon: 'UserGroupIcon',
        href: '/bulk-orders',
        roles: ['user'],
        order: 16,
      },
      {
        section: 'Orders',
        title: 'Orders',
        description: 'Order history',
        icon: 'DocumentTextIcon',
        href: '/orders',
        roles: ['user'],
        order: 20,
      },
      {
        section: 'Orders',
        title: 'Checker History',
        description: 'WAEC voucher purchase history & PINs',
        icon: 'ClipboardDocumentListIcon',
        href: '/waec/history',
        roles: ['user'],
        order: 22,
      },
      {
        section: 'Orders',
        title: 'Saved Numbers',
        description: 'Manage saved recipient numbers',
        icon: 'PhoneIcon',
        href: '/beneficiaries',
        roles: ['user'],
        order: 23,
      },
      {
        section: 'Orders',
        title: 'Price Watchlist',
        description: 'Track price drops on packages',
        icon: 'BellIcon',
        href: '/watchlist',
        roles: ['user'],
        order: 24,
      },
      {
        section: 'Orders',
        title: 'Bulk Order History',
        description: 'View bulk order history',
        icon: 'ClipboardDocumentListIcon',
        href: '/bulk-orders/history',
        roles: ['user'],
        order: 21,
      },
      {
        section: 'Orders',
        title: 'Rewards',
        description: 'Earn and redeem loyalty points',
        icon: 'SparklesIcon',
        href: '/loyalty-points',
        roles: ['user'],
        order: 25,
      },
      {
        section: 'Wallet',
        title: 'Wallet',
        description: 'Wallet balance',
        icon: 'WalletIcon',
        href: '/wallet',
        roles: ['user'],
        order: 30,
      },
      {
        section: 'Wallet',
        title: 'Deposit',
        description: 'Add funds to your wallet',
        icon: 'BanknotesIcon',
        href: '/wallet?topup=1',
        roles: ['user'],
        order: 31,
      },
      {
        section: 'Wallet',
        title: 'Wallet Transactions',
        description: 'Payment and transaction history',
        icon: 'CreditCardIcon',
        href: '/payments',
        roles: ['user'],
        order: 32,
      },
      {
        section: 'Become an Agent',
        title: 'Become an Agent',
        description: 'Start agent onboarding',
        icon: 'UserGroupIcon',
        href: '/become-agent',
        roles: ['user'],
        order: 40,
      },
      {
        section: 'Settings',
        title: 'Profile',
        description: 'View and edit your profile',
        icon: 'UserIcon',
        href: '/profile',
        roles: ['user'],
        order: 41,
      },
    ];
  }, [hasFeature]);

  const navigationItems = React.useMemo(() => getRoleNavigation(user), [getRoleNavigation, user]);

  const adminSectionOrder = React.useMemo(
    () => [
      'Dashboard',
      'Users & Roles',
      'Orders & Sales',
      'Wallet & Finance',
      'Packages & Products',
      'Analytics & Reports',
      'System Configuration',
    ],
    []
  );

  const standardizedNavigationItems = React.useMemo(() => {
    const userRole = user?.role || 'user';

    if (userRole === 'admin') {
      const sectionRank = new Map(adminSectionOrder.map((s, idx) => [s, idx]));

      return (navigationItems || [])
        .map((item: any) => ({
          ...item,
          name: String(item?.title || item?.name || ''),
        }))
        .sort((a: any, b: any) => {
          const aOrder = typeof a.order === 'number' ? a.order : 999;
          const bOrder = typeof b.order === 'number' ? b.order : 999;
          const aSection = String(a.section || '');
          const bSection = String(b.section || '');
          const aSectionRank = sectionRank.get(aSection) ?? 999;
          const bSectionRank = sectionRank.get(bSection) ?? 999;
          if (aSectionRank !== bSectionRank) return aSectionRank - bSectionRank;
          return aOrder - bOrder;
        });
    }

    const normalizeLabel = (href: string, current: string) => {
      const labelByHref: Record<string, string> = {
        '/dashboard': 'Dashboard',
        '/packages': 'Data Bundles',
        '/airtime': 'Airtime',
        '/mtn-afa': 'MTN AFA Registrations',
        '/waec': 'WAEC Services',
        '/waec/history': 'Checker History',
        '/checker-history': 'Checker History',
        '/watchlist': 'Price Watchlist',
        '/loyalty-points': 'Rewards',
        '/orders': 'Data Orders',
        '/bulk-orders': 'Bulk Orders',
        '/bulk-orders/history': 'Bulk Order History',
        '/wallet': 'Wallet Overview',
        '/wallet?topup=1': 'Deposit',
        '/payments': 'Wallet Transactions',
        '/withdrawals': 'Withdrawals',
        '/referrals': 'Referrals',
        '/commissions': 'Commission Reports',
        '/storefront': 'Storefront',
        '/customers': 'Customers',
        '/profile': 'Profile',
        '/settings': 'Settings',
        '/admin': 'Admin Dashboard',
        '/admin/users': 'Users',
        '/admin/users/export': 'Export Center',
        '/admin/packages': 'Data Bundles',
        '/admin/orders': 'Data Orders',
        '/admin/airtime-orders': 'Airtime Orders',
        '/admin/payments': 'Payment History',
        '/admin/wallet-topup': 'Wallet Top-ups',
        '/admin/withdrawals': 'Withdrawals',
        '/admin/agent-payment': 'Agent Registration Fees',
        '/admin/commissions': 'Agent Commissions',
        '/admin/settings': 'System Settings',
        '/admin/announcements': 'Announcements',
        '/admin/delivery-status': 'Delivery Status',
        '/admin/payment-verification': 'Payment Verification',
        '/admin/waec-stock': 'WAEC Stock',
        '/admin/mtn-afa': 'MTN AFA Requests',
        '/admin/pricing': 'Pricing Configuration',
        '/admin/sales-ladder': 'Sales Ladder',
        '/admin/telegram': 'Notification Integrations',
        '/admin/analytics': 'Sales Reports',
        '/admin/routing': 'Supplier Routing',
        '/admin/backfill-diagnostics': 'Backfill Diagnostics',
        
      };

      return labelByHref[href] || labelByHref[String(href || '').split('#')[0]] || current;
    };

    const isAllowedByRole = (href: string) => {
      const hrefPath = String(href || '').split('#')[0];
      // At this point we're in the non-admin branch (admin has an early return above)
      if (hrefPath.startsWith('/admin')) return false;
      if (hrefPath === '/storefront' || hrefPath === '/customers') return userRole === 'agent';
      // Allow all users to access all navigation items based on their role permissions
      // Role-based filtering is handled by the roles array in each navigation item
      return true;
    };

    const resolveSection = (href: string) => {
      const hrefPath = String(href || '').split('#')[0];
      const hrefPathWithoutQuery = hrefPath.split('?')[0];
      if (hrefPathWithoutQuery === '/dashboard' || hrefPathWithoutQuery === '/admin' || hrefPathWithoutQuery === '/agent') return 'Dashboard';

      // Services
      if (
        hrefPath === '/packages' ||
        hrefPath.startsWith('/packages/') ||
        hrefPath === '/airtime' ||
        hrefPath === '/mtn-afa' ||
        hrefPath === '/waec' ||
        hrefPath === '/bulk-orders' ||
        hrefPath.startsWith('/bulk-orders') ||
        hrefPath === '/admin/packages' ||
        hrefPath === '/admin/waec-stock' ||
        hrefPath === '/admin/mtn-afa'
      ) {
        return 'Services';
      }

      // Financial
      if (
        hrefPath === '/wallet' ||
        hrefPath === '/payments' ||
        hrefPath === '/withdrawals' ||
        hrefPath === '/admin/payments' ||
        hrefPath === '/admin/wallet-topup' ||
        hrefPath === '/admin/withdrawals' ||
        hrefPath === '/admin/payment-verification'
      ) {
        return 'Financial';
      }

      // Business
      if (
        hrefPath === '/orders' ||
        hrefPath.startsWith('/orders/') ||
        hrefPath === '/referrals' ||
        hrefPath === '/commissions' ||
        hrefPath === '/storefront' ||
        hrefPath === '/customers' ||
        hrefPath === '/admin/orders'
      ) {
        return 'Business';
      }

      // Watchlist
      if (hrefPath === '/watchlist') {
        return 'Orders';
      }

      // Administration
      if (
        hrefPath === '/admin/users' ||
        hrefPath === '/admin/users/export' ||
        hrefPath === '/admin/withdrawals'
      ) {
        return 'Administration';
      }

      // Settings
      if (
        hrefPath === '/profile' ||
        hrefPath === '/settings' ||
        hrefPath === '/admin/settings' ||
        hrefPath === '/admin/pricing' ||
        hrefPath === '/admin/agent-payment' ||
        hrefPath === '/admin/telegram' ||
        hrefPath === '/admin/announcements' ||
        hrefPath === '/admin/delivery-status'
      ) {
        return 'Settings';
      }

      return 'Dashboard';
    };

    const normalized = (navigationItems || [])
      .filter((item: any) => {
        const href = String(item?.href || '');
        const hrefPath = href.split('#')[0];
        if (!href) return false;
        if (!isAllowedByRole(hrefPath)) return false;

        if (Array.isArray(item?.roles) && item.roles.length > 0) {
          return item.roles.includes(userRole);
        }

        return true;
      })
      .map((item: any) => {
        const href = String(item.href);
        const hrefPath = href.split('#')[0];
        const rawTitle = String(item.title || item.name || '');
        const title = normalizeLabel(hrefPath, rawTitle);
        const section = item.section ? String(item.section) : resolveSection(hrefPath);
        return {
          ...item,
          section,
          title,
          name: title
        };
      });

    const sectionOrder = userRole === 'agent'
      ? ['Dashboard', 'Services', 'Orders', 'Wallet', 'Settings']
      : ['Dashboard', 'Services', 'Business', 'Orders', 'Wallet', 'Become an Agent'];

    const sectionRank = new Map(sectionOrder.map((s, idx) => [s, idx]));

    return normalized.sort((a: any, b: any) => {
      const aSection = String(a.section || '');
      const bSection = String(b.section || '');
      const aSectionRank = sectionRank.get(aSection) ?? 999;
      const bSectionRank = sectionRank.get(bSection) ?? 999;
      if (aSectionRank !== bSectionRank) return aSectionRank - bSectionRank;
      const aOrder = typeof a.order === 'number' ? a.order : 999;
      const bOrder = typeof b.order === 'number' ? b.order : 999;
      return aOrder - bOrder;
    });
  }, [adminSectionOrder, navigationItems, user?.role]);

  // Icon mapping for backend navigation items
  const getIcon = (iconName: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      'HomeIcon': HomeIcon,
      'DevicePhoneMobileIcon': DevicePhoneMobileIcon,
      'DocumentTextIcon': DocumentTextIcon,
      'BuildingOfficeIcon': BuildingOfficeIcon,
      'WalletIcon': WalletIcon,
      'CreditCardIcon': CreditCardIcon,
      'GiftIcon': GiftIcon,
      'CurrencyDollarIcon': CurrencyDollarIcon,
      'BanknotesIcon': BanknotesIcon,
      'ArrowTrendingUpIcon': ArrowTrendingUpIcon,
      'ChartBarIcon': ChartBarIcon,
      'BellIcon': BellIcon,
      'UserGroupIcon': UserGroupIcon,
      'ShoppingCartIcon': ShoppingCartIcon,
      'ShoppingBagIcon': ShoppingBagIcon,
      'UsersIcon': UsersIcon,
      'CubeIcon': CubeIcon,
      'ClipboardDocumentListIcon': ClipboardDocumentListIcon,
      'CogIcon': CogIcon,
      'UserIcon': UserIcon,
      'ShieldCheckIcon': ShieldCheckIcon,
      'BugAntIcon': BugAntIcon,
      'PhoneIcon': PhoneIcon,
      'SparklesIcon': SparklesIcon,
      'TagIcon': TagIcon,
    };
    return iconMap[iconName] || HomeIcon;
  };

  const adminNavigationGroups = React.useMemo(() => {
    if (user?.role !== 'admin') return [] as Array<{ section: string; items: any[] }>;

    const groupOrder = [
      'Dashboard & Quick Actions',
      'Sales & Revenue',
      'Orders & Transactions',
      'Wallet & Finance',
      'Users & Agents',
      'System / Settings / Support'
    ];

    const groupRank = new Map(groupOrder.map((s, idx) => [s, idx]));

    const normalizeAdminGroup = (item: any) => {
      const hrefPath = String(item?.href || '').split('#')[0];

      if (hrefPath === '/admin') return 'Dashboard & Quick Actions';

      if (hrefPath === '/admin/analytics' || hrefPath === '/admin/sales-ladder') return 'Sales & Revenue';
      if (hrefPath === '/admin/packages' || hrefPath === '/admin/pricing' || hrefPath === '/admin/agent-payment') {
        return 'Sales & Revenue';
      }

      if (hrefPath === '/admin/orders' || hrefPath === '/admin/airtime-orders' || hrefPath === '/admin/bulk-orders' || hrefPath === '/admin/payments') {
        return 'Orders & Transactions';
      }
      if (hrefPath === '/admin/mtn-afa' || hrefPath === '/admin/waec-stock') {
        return 'Orders & Transactions';
      }

      if (hrefPath === '/admin/wallet-topup' || hrefPath === '/admin/withdrawals' || hrefPath === '/admin/payment-verification') {
        return 'Wallet & Finance';
      }

      if (hrefPath === '/admin/commissions') {
        return 'Wallet & Finance';
      }

      if (hrefPath === '/admin/users') return 'Users & Agents';

      if (
        hrefPath === '/admin/settings' ||
        hrefPath === '/admin/telegram' ||
        hrefPath === '/admin/announcements' ||
        hrefPath === '/admin/delivery-status'
      ) {
        return 'System / Settings / Support';
      }

      return 'System / Settings / Support';
    };

    const deduped = (standardizedNavigationItems || [])
      .filter((item) => String(item?.href || '').split('#')[0] !== '/admin/config')
      .filter((item, idx, arr) => {
        const href = String(item?.href || '').split('#')[0];
        return arr.findIndex((x) => String(x?.href || '').split('#')[0] === href) === idx;
      })
      .map((item) => ({
        ...item,
        section: normalizeAdminGroup(item)
      }));

    const groups = new Map<string, any[]>();
    for (const item of deduped) {
      const section = String(item?.section || '');
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section)!.push(item);
    }

    return Array.from(groups.entries())
      .sort(([aSection], [bSection]) => {
        const aRank = groupRank.get(String(aSection)) ?? 999;
        const bRank = groupRank.get(String(bSection)) ?? 999;
        return aRank - bRank;
      })
      .map(([section, items]) => ({
        section,
        items: (items || []).sort((a, b) => {
          const aOrder = typeof a.order === 'number' ? a.order : 999;
          const bOrder = typeof b.order === 'number' ? b.order : 999;
          return aOrder - bOrder;
        })
      }));
  }, [standardizedNavigationItems, user?.role]);

  const userNavigationGroups = React.useMemo(() => {
    if (user?.role === 'admin') return [] as Array<{ section: string; items: any[] }>;

    const sectionOrder = user?.role === 'agent'
      ? ['Dashboard', 'Services', 'Orders', 'Wallet', 'Settings']
      : ['Dashboard', 'Services', 'Business', 'Orders', 'Wallet', 'Settings', 'Become an Agent'];

    const groupRank = new Map(sectionOrder.map((s, idx) => [s, idx]));

    const groups = new Map<string, any[]>();
    for (const item of standardizedNavigationItems || []) {
      const section = String(item?.section || '');
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section)!.push(item);
    }

    return Array.from(groups.entries())
      .sort(([aSection], [bSection]) => {
        const aRank = groupRank.get(String(aSection)) ?? 999;
        const bRank = groupRank.get(String(bSection)) ?? 999;
        return aRank - bRank;
      })
      .map(([section, items]) => ({
        section,
        items: (items || []).sort((a, b) => {
          const aOrder = typeof a.order === 'number' ? a.order : 999;
          const bOrder = typeof b.order === 'number' ? b.order : 999;
          return aOrder - bOrder;
        })
      }));
  }, [standardizedNavigationItems, user?.role]);

  // Initialize collapsed sections: user & agent start collapsed for a denser look,
  // admin sections start expanded (existing behavior)
  React.useEffect(() => {
    const groups = user?.role === 'admin' ? adminNavigationGroups : userNavigationGroups;
    if (!groups || groups.length === 0) return;

    setCollapsedSections((prev) => {
      const next = { ...prev };
      let changed = false;
      const shouldCollapse = user?.role !== 'admin';
      for (const group of groups) {
        if (!(group.section in prev)) {
          next[group.section] = shouldCollapse;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [user?.role, adminNavigationGroups, userNavigationGroups]);

  // Auto-expand the group containing the currently active route and collapse all others (all roles)
  React.useEffect(() => {
    const groups = user?.role === 'admin' ? adminNavigationGroups : userNavigationGroups;
    if (!groups || groups.length === 0) return;

    const active = standardizedNavigationItems.find((item: any) => isActive(item?.href));
    const activeHref = String(active?.href || '').split('#')[0];
    if (!activeHref) return;

    const activeGroup = groups.find((g) =>
      (g.items || []).some((i: any) => String(i?.href || '').split('#')[0] === activeHref)
    );

    setCollapsedSections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const group of groups) {
        const shouldExpand = activeGroup?.section === group.section;
        const newValue = !shouldExpand; // collapse all except active
        if (prev[group.section] !== newValue) {
          next[group.section] = newValue;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [adminNavigationGroups, userNavigationGroups, isActive, standardizedNavigationItems, user?.role]);

  // Render single navigation item with modern professional design and proper text overflow handling
  const renderNavItem = (item: any) => {
    const IconComponent = getIcon(item.icon);
    const isItemActive = isActive(item.href);
    const itemName = item.name || item.title;
    const itemDescription = item.description;
    
    return (
      <div className="group relative" title={itemDescription || itemName} key={String(item.href || itemName)}>
        <NavLink
          key={itemName}
          to={item.href}
          onClick={() => onClose()}
          className={({ isActive: linkActive }) =>
            clsx(
              'group flex items-center px-2 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-in-out ml-1 min-h-[2.75rem] w-full',
              linkActive
                ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/20 border border-primary-400/30'
                : 'text-slate-700 dark:text-slate-200 hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 dark:hover:from-navy-700/40 dark:hover:to-navy-600/40 hover:text-slate-900 dark:hover:text-white hover:shadow-md hover:border hover:border-slate-200/50 dark:hover:border-navy-600/50'
            )
          }
        >
          <IconComponent
            className={clsx(
              'mr-2 h-4 w-4 flex-shrink-0 transition-all duration-200',
              isItemActive
                ? 'text-white drop-shadow-sm'
                : 'text-slate-500 dark:text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
            )}
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex-1 min-w-0 overflow-hidden">
                <span className={clsx(
                  'block truncate font-semibold text-sm leading-tight',
                  isItemActive 
                    ? 'text-white drop-shadow-sm' 
                    : 'text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white'
                )}>
                  {itemName}
                </span>
                {itemDescription && (
                  <span className={clsx(
                    'block truncate text-xs font-normal mt-0.5 leading-tight',
                    isItemActive 
                      ? 'text-blue-100' 
                      : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300'
                  )}>
                    {itemDescription}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-end ml-2 flex-shrink-0">
                {item.badge && (
                  <span className={clsx(
                    "inline-flex items-center px-1.5 sm:px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider rounded-full flex-shrink-0 transition-all duration-200 max-w-[7rem] truncate shadow-sm",
                    isItemActive
                      ? 'bg-white text-orange-600'
                      : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white group-hover:shadow-md'
                  )}>
                    {item.badge}
                  </span>
                )}
              </div>
            </div>
          </div>
        </NavLink>
        
        {/* Professional tooltip with enhanced styling and mobile responsiveness */}
        <div className={clsx(
          'absolute left-full ml-3 px-3 py-2.5 bg-gradient-to-br from-navy-900 to-navy-800 dark:from-navy-800 dark:to-navy-900 text-white text-sm rounded-xl shadow-2xl border border-navy-700/50 dark:border-navy-600/50 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm',
          'top-1/2 -translate-y-1/2 transform',
          // Mobile-first responsive positioning
          'md:left-full md:ml-3 md:top-1/2 md:-translate-y-1/2',
          'max-md:left-1/2 max-md:-translate-x-1/2 max-md:bottom-full max-md:mb-2 max-md:top-auto max-md:max-w-[200px]',
          // Enhanced tooltip arrow
          'md:before:content-[\'\'] md:before:absolute md:before:right-full md:before:top-1/2 md:before:-translate-y-1/2 md:before:border-4 md:before:border-transparent md:before:border-r-navy-800 dark:md:before:border-r-navy-700',
          'max-md:before:content-[\'\'] max-md:before:absolute max-md:before:left-1/2 max-md:before:-translate-x-1/2 max-md:before:bottom-full max-md:before:top-auto max-md:before:border-4 max-md:before:border-transparent max-md:before:border-b-navy-800 dark:max-md:before:border-b-navy-700'
        )}>
          <div className="flex items-start space-x-2">
            <IconComponent className="h-4 w-4 text-primary-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-100 truncate">{itemName}</div>
              {itemDescription && (
                <div className="text-xs text-slate-300 dark:text-slate-400 mt-1 leading-relaxed">{itemDescription}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render a collapsible navigation group used by all roles (user, agent, admin)
  const renderGroup = (group: { section: string; items: any[] }) => {
    const isCollapsed = !!collapsedSections[group.section];
    const groupId = `nav-group-${String(group.section).toLowerCase().replace(/\s+/g, '-')}`;

    return (
      <div key={group.section} className="space-y-1">
        <button
          type="button"
          onClick={() =>
            setCollapsedSections((prev) => ({
              ...prev,
              [group.section]: !prev[group.section]
            }))
          }
          aria-expanded={!isCollapsed}
          aria-controls={groupId}
          className="w-full flex items-center justify-between px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
        >
          <span className="truncate">{group.section}</span>
          <ChevronDownIcon
            className={clsx(
              'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200',
              isCollapsed ? '-rotate-90' : 'rotate-0'
            )}
            aria-hidden="true"
          />
        </button>
        {!isCollapsed && (
          <div id={groupId} className="space-y-1">
            {group.items.map(renderNavItem)}
          </div>
        )}
      </div>
    );
  };

  // Logo component with mobile-first responsive design
  const Logo = () => (
    <BrandLogo size="sm" showText />
  );

  // User info component with mobile-first responsive design
  const UserInfo = () => {
    if (!user?.username && !user?.email) {
      return null;
    }
    
    return (
      <div className="px-2.5 sm:px-3 py-2.5 sm:py-3 border-t border-gray-200 dark:border-navy-700">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-br from-navy-600 to-navy-800 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-xs">
                {user?.username?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
          </div>
          <div className="ml-2 sm:ml-3 flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
              {user?.username}
            </p>
            {user?.email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user.email}
              </p>
            )}
            {user?.role && (
              <Badge
                size="sm"
                variant={user.role === 'admin' ? 'danger' : user.role === 'agent' ? 'secondary' : 'info'}
                className={clsx(
                  'mt-1 capitalize inline-flex items-center gap-1',
                  user.role === 'agent' && 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                )}
              >
                <UserIcon className="h-3 w-3" />
                <span>{user.role}</span>
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Help button component with mobile-first responsive design
  const HelpButton = () => {
    return (
      <button
        onClick={() => setIsHelpOpen(true)}
        className="group flex items-center w-full px-2.5 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-400 transition-all duration-200 ease-in-out mb-1"
      >
        <QuestionMarkCircleIcon
          className="mr-2 sm:mr-3 h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200"
          aria-hidden="true"
        />
        <span className="flex-1 text-left truncate">Help & Support</span>
        <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex-shrink-0">
          New
        </span>
      </button>
    );
  };

  // Logout button component with mobile-first responsive design
  const LogoutButton = () => (
    <button
      onClick={logout}
      className="group flex items-center w-full px-2.5 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-400 transition-all duration-200 ease-in-out"
    >
      <ArrowRightOnRectangleIcon
        className="mr-2 sm:mr-3 h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-200"
        aria-hidden="true"
      />
      <span className="flex-1 text-left truncate">Logout</span>
    </button>
  );

  // Mobile bottom navigation bar for richer mobile experience
  const MobileBottomNav = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dashboardHref = user?.role === 'agent' ? '/agent' : '/dashboard';

    const tabs = [
      { href: dashboardHref, label: 'Dashboard', icon: HomeIcon },
      { href: '/packages', label: 'Services', icon: CubeIcon },
      { href: '/orders', label: 'Orders', icon: DocumentTextIcon },
      { href: '/wallet', label: 'Wallet', icon: WalletIcon },
      { href: '/settings', label: 'Settings', icon: CogIcon },
    ];

    return (
      <nav className="fixed bottom-0 inset-x-0 z-50 lg:hidden bg-gradient-to-t from-slate-50/95 to-slate-50 dark:from-navy-800 dark:to-navy-950 border-t border-slate-200/50 dark:border-navy-700/50 backdrop-blur-md shadow-lg safe-area-bottom">
        <div className="flex items-center justify-around h-14 px-2 sm:px-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.href ||
              (tab.href === dashboardHref &&
                (location.pathname === '/dashboard' || location.pathname === '/agent'));
            return (
              <button
                key={tab.label}
                onClick={() => {
                  navigate(tab.href);
                  onClose();
                }}
                aria-pressed={isActive}
                className="flex flex-col items-center justify-center gap-1 flex-1 min-w-0"
              >
                <Icon
                  className={clsx(
                    'h-4 w-4 flex-shrink-0 transition-colors duration-200',
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-slate-400 dark:text-slate-500 hover:text-primary-600 dark:hover:text-primary-400'
                  )}
                  aria-hidden="true"
                />
                <span
                  className={clsx(
                    'text-[10px] font-medium leading-none transition-colors duration-200',
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400'
                  )}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    );
  };

  return (
    <>
      {/* Mobile sidebar with modern backdrop */}
      <div className={clsx(
        'fixed inset-0 z-50 flex lg:hidden',
        open ? 'flex' : 'hidden'
      )}>
        {/* Enhanced backdrop with blur */}
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
        
        {/* Modern Mobile Sidebar */}
        <div className="relative flex-1 flex flex-col w-72 max-w-[85vw] bg-gradient-to-b from-white to-slate-50 dark:from-navy-800 dark:to-navy-950 shadow-2xl border-r border-slate-200/50 dark:border-navy-700/50">
          <div className="absolute top-2 right-2">
            <button
              onClick={onClose}
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 bg-white/90 dark:bg-navy-900/60 backdrop-blur-sm hover:bg-white dark:hover:bg-navy-900"
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="h-5 w-5 text-gray-700 dark:text-gray-100" aria-hidden="true" />
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center h-14 sm:h-16 px-3 sm:px-4 border-b border-gray-200 dark:border-navy-700 min-w-0">
            <Logo />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 sm:px-3 py-3 sm:py-4 space-y-2 overflow-y-auto pb-20">
            {user?.role === 'admin' ? (
              <div className="space-y-4">
                {adminNavigationGroups.map(renderGroup)}
              </div>
            ) : (
              <div className="space-y-4">
                {userNavigationGroups.map(renderGroup)}
              </div>
            )}
          </nav>

          {/* User Info */}
          <UserInfo />

          {/* Footer */}
          <div className="p-2 sm:p-3 border-t border-gray-200 dark:border-navy-700">
            <div className="space-y-1">
              <HelpButton />
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom navigation bar for richer mobile experience */}
      <MobileBottomNav />

      {/* Modern Desktop sidebar with responsive design */}
      <div className="hidden lg:flex lg:w-56 xl:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-gradient-to-b from-white to-slate-50 dark:from-navy-800 dark:to-navy-950 border-r border-slate-200/50 dark:border-navy-700/50 shadow-xl">
          {/* Modern Header */}
          <div className="flex items-center h-14 xl:h-16 px-3 xl:px-4 border-b border-slate-200/50 dark:border-navy-700/50 bg-gradient-to-r from-slate-50 to-white dark:from-navy-800 dark:to-navy-900">
            <Logo />
          </div>

          {/* Enhanced Navigation */}
          <nav className="flex-1 px-2 xl:px-3 py-3 xl:py-4 space-y-2 overflow-y-auto pb-20">
            {user?.role === 'admin' ? (
              <div className="space-y-4">
                {adminNavigationGroups.map(renderGroup)}
              </div>
            ) : (
              <div className="space-y-4">
                {userNavigationGroups.map(renderGroup)}
              </div>
            )}
          </nav>

          {/* Modern User Info */}
          <UserInfo />

          {/* Enhanced Footer */}
          <div className="p-2 xl:p-3 border-t border-slate-200/50 dark:border-navy-700/50 bg-gradient-to-r from-slate-50 to-white dark:from-navy-800 dark:to-navy-900">
            <div className="space-y-1">
              <HelpButton />
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>

      {/* Help Center Modal */}
      <HelpCenter isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </>
  );
};

export default Sidebar;
